/**
 * Typed client over the wePAY Firebase Cloud Function callables.
 *
 * All callables are deployed in `asia-southeast1` and gated by the
 * `users/{uid}.role === 'admin'` check on the server. The browser must be
 * signed into Firebase Auth (see `client/src/lib/firebase.ts`).
 *
 * This module replaces the legacy tRPC `wepaySync.*` mutations on the BOS
 * Game Sync page. Each function returns the raw response shape from the
 * corresponding Cloud Function — Firestore Timestamps come through as plain
 * `{ _seconds, _nanoseconds }` objects when JSON-serialised; convert with
 * `timestampToDate()` before rendering.
 */

import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';

import { getFirebaseApp } from './firebase';

const REGION = 'asia-southeast1';

let _functions: Functions | null = null;

function getWepayFunctions(): Functions {
  if (_functions) return _functions;
  const app = getFirebaseApp();
  if (!app) {
    throw new Error(
      'Firebase is not configured — VITE_FIREBASE_API_KEY / VITE_FIREBASE_PROJECT_ID must be set',
    );
  }
  _functions = getFunctions(app, REGION);
  return _functions;
}

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

/** Firestore Timestamp as it arrives over the callable wire. */
export interface FirestoreTimestampLike {
  _seconds: number;
  _nanoseconds?: number;
}

export type IsoOrTimestamp = string | FirestoreTimestampLike | null | undefined;

/**
 * Convert a value that may be an ISO string, a Firestore Timestamp-like
 * object, or null/undefined into a JS Date (or null).
 */
export function timestampToDate(value: IsoOrTimestamp): Date | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000);
  }
  return null;
}

// ---------------------------------------------------------------------------
// wepayCatalogSync — manual sync (writes to Firestore)
// ---------------------------------------------------------------------------

export interface SyncResult {
  success: boolean;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  companiesProcessed: number;
  gamesAdded: number;
  gamesUpdated: number;
  packagesAdded: number;
  packagesUpdated: number;
  errors: string[];
  triggeredBy: string;
}

export async function wepayCatalogSync(): Promise<SyncResult> {
  const fn = httpsCallable<Record<string, never>, SyncResult>(
    getWepayFunctions(),
    'wepayCatalogSync',
  );
  const res = await fn({});
  return res.data;
}

// ---------------------------------------------------------------------------
// wepayCatalogPreview — dry-run diff for the BOS modal
// ---------------------------------------------------------------------------

export interface CatalogDiff {
  newGames: Array<{ gameKey: string; serviceName: string; amounts: number[] }>;
  newPackages: Array<{
    gameKey: string;
    gameName: string;
    amount: number;
    description: string;
  }>;
  priceChanges: Array<{
    gameKey: string;
    gameName: string;
    amount: number;
    oldPrice: number;
    newPrice: number;
  }>;
  removedPackages: Array<{ gameKey: string; gameName: string; amount: number }>;
}

export interface PreviewResult {
  diff: CatalogDiff;
  companiesProcessed: number;
  errors: string[];
  computedAt: string;
}

export async function wepayCatalogPreview(): Promise<PreviewResult> {
  const fn = httpsCallable<Record<string, never>, PreviewResult>(
    getWepayFunctions(),
    'wepayCatalogPreview',
  );
  const res = await fn({});
  return res.data;
}

// ---------------------------------------------------------------------------
// wepaySyncHistory — recent runs
// ---------------------------------------------------------------------------

/**
 * Sync run as returned over the wire by `wepaySyncHistory`. The server stores
 * `startedAt`/`finishedAt` as Firestore Timestamps which serialise to
 * `{ _seconds, _nanoseconds }` — convert with `timestampToDate()` before
 * rendering.
 */
export interface SyncRun extends Omit<SyncResult, 'startedAt' | 'finishedAt'> {
  id: string;
  startedAt: IsoOrTimestamp | string;
  finishedAt: IsoOrTimestamp | string;
}

export interface SyncHistoryResponse {
  runs: SyncRun[];
}

export async function wepaySyncHistory(input?: {
  limit?: number;
}): Promise<SyncHistoryResponse> {
  const fn = httpsCallable<{ limit?: number }, SyncHistoryResponse>(
    getWepayFunctions(),
    'wepaySyncHistory',
  );
  const res = await fn(input ?? {});
  return res.data;
}

// ---------------------------------------------------------------------------
// wepaySyncStatus — header status card data
// ---------------------------------------------------------------------------

export interface SyncStatusResponse {
  totalGames: number;
  totalCompaniesConfigured: number;
  lastRun: SyncRun | null;
  apiConfigured: boolean;
  whitelistedIp: string;
}

export async function wepaySyncStatus(): Promise<SyncStatusResponse> {
  const fn = httpsCallable<Record<string, never>, SyncStatusResponse>(
    getWepayFunctions(),
    'wepaySyncStatus',
  );
  const res = await fn({});
  return res.data;
}

// ---------------------------------------------------------------------------
// wepayBalanceInquiry
// ---------------------------------------------------------------------------

export interface BalanceResponse {
  code: string;
  ledger_balance?: string;
  available_balance?: number;
  desc?: string;
}

export async function wepayBalanceInquiry(): Promise<BalanceResponse> {
  const fn = httpsCallable<Record<string, never>, BalanceResponse>(
    getWepayFunctions(),
    'wepayBalanceInquiry',
  );
  const res = await fn({});
  return res.data;
}

// ---------------------------------------------------------------------------
// wepaySeedCompanies — bootstrap wepay_companies/{code}
// ---------------------------------------------------------------------------

export type WepayServiceType = 'mtopup' | 'cashcard' | 'billpay';

export interface SeedCompaniesInput {
  companies?: Array<{
    code: string;
    type: WepayServiceType;
    gameKey: string;
    enabled?: boolean;
  }>;
  overwrite?: boolean;
}

export interface SeedCompaniesResponse {
  added: number;
  updated: number;
  skipped: number;
  total: number;
}

export async function wepaySeedCompanies(
  input?: SeedCompaniesInput,
): Promise<SeedCompaniesResponse> {
  const fn = httpsCallable<SeedCompaniesInput, SeedCompaniesResponse>(
    getWepayFunctions(),
    'wepaySeedCompanies',
  );
  const res = await fn(input ?? {});
  return res.data;
}

// ---------------------------------------------------------------------------
// wepayMockCallback
// ---------------------------------------------------------------------------

export interface MockCallbackInput {
  destRef: string;
  succeed?: boolean;
  callbackUrl?: string;
}

export interface MockCallbackResponse {
  url: string;
  status: number;
  body: string;
  sent: Record<string, string>;
}

export async function wepayMockCallback(
  input: MockCallbackInput,
): Promise<MockCallbackResponse> {
  const fn = httpsCallable<MockCallbackInput, MockCallbackResponse>(
    getWepayFunctions(),
    'wepayMockCallback',
  );
  const res = await fn(input);
  return res.data;
}

// ---------------------------------------------------------------------------
// wepayCreateMockTopup
// ---------------------------------------------------------------------------

export interface CreateMockTopupInput {
  payToCompany: string;
  payToAmount: number;
  payToRef1: string;
  type?: WepayServiceType;
  autoCallback?: boolean;
  succeed?: boolean;
}

export interface CreateMockTopupResponse {
  destRef: string;
  dispatch: {
    code: string;
    desc?: string;
    bill_id?: number;
    transaction_id?: number;
    queue_id?: number;
    total_amount?: number;
    balance?: number;
  };
  callback: { status: number; body: string } | null;
  firestore: { collection: string; id: string } | null;
}

export async function wepayCreateMockTopup(
  input: CreateMockTopupInput,
): Promise<CreateMockTopupResponse> {
  const fn = httpsCallable<CreateMockTopupInput, CreateMockTopupResponse>(
    getWepayFunctions(),
    'wepayCreateMockTopup',
  );
  const res = await fn(input);
  return res.data;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Sum the four buckets in a CatalogDiff. Used by the "Pending updates" badge.
 */
export function diffPendingCount(diff: CatalogDiff): number {
  return (
    diff.newGames.length +
    diff.newPackages.length +
    diff.priceChanges.length +
    diff.removedPackages.length
  );
}
